import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import T from "i18n-react";
import ChoiceBox from "../ChoiceBox/ChoiceBox";
import Styles from "./styles.module.scss";
import { setDisplayeExtrasAllergens } from "../state/ProductDetails.action";

interface IExtrasProps {
  extrasData: IMenuExtra;
  extrasId?: string;
  handleExtrasChange: (id: string, extras: ISelectedExtras[]) => void;
  extrasState?: ISelectedExtras[];
  canContinue?: (isContinue: boolean) => void;
  dispatch: any;
}

interface IExtrasSelected {
  extras: ISelectedExtras[];
  //ids for quick searching
  ids: string[];
}
/**Handles the extras */
function Extras({
  extrasData,
  handleExtrasChange,
  extrasId,
  extrasState,
  canContinue,
  dispatch,
}: IExtrasProps) {
  const menuItemExtras = useSelector((state: any) => state.MenuItemsAsExtra);
  const [selectedExtras, setSelectedExtras] = useState<IExtrasSelected>({
    extras: [],
    ids: [],
  });
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (extrasState.length > 0) {
      const ids = extrasState.map((value) => value.id);
      setSelectedExtras({ extras: extrasState, ids });
    } else {
      if (extrasData.menu_item_extra_group_quantity_min >= 1) {
        canContinue(false);
      }
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (selectedExtras) {
      handleExtrasChange(extrasId, selectedExtras.extras);
      let ExtraMin = extrasData.menu_item_extra_group_quantity_min;
      // if Max is 0 or null that means there are no limitations so we assume max is equal to total number of extra options
      let ExtraMax = extrasData.menu_item_extra_group_quantity_max
        ? extrasData.menu_item_extra_group_quantity_max
        : extrasData?.menu_item_extra_group_item_id_list?.length;

      if (
        selectedExtras.ids.length >= ExtraMin &&
        selectedExtras.ids.length <= ExtraMax
      ) {
        canContinue(true);
      } else {
        if (extrasState.length === 0) {
          canContinue(false);
        } else if (selectedExtras.ids.length < ExtraMax) {
          canContinue(false);
        }
      }
    }
    // eslint-disable-next-line
  }, [selectedExtras]);
  const DisplayeExtrasAllergens = (menuItem: any) => {
    dispatch(setDisplayeExtrasAllergens(menuItem, true));
  };
  const findItemInList = (id: string) => {
    const { ids } = selectedExtras;
    if (ids.length === 0) {
      return false;
    }
    if (ids.indexOf(id) > -1) {
      return true;
    }
    return false;
  };
  const handleExtraChange = (
    menuItem: any,
    pricingOptionName: string,
    price: number
  ) => {
    const { extras, ids } = selectedExtras;
    const menuId = menuItem.extra_id;
    if (findItemInList(menuId) === true) {
      const index = ids.indexOf(menuId);
      extras.splice(index, 1);
      setSelectedExtras((prevState: IExtrasSelected) => ({
        extras,
        ids: extras.map((ex) => ex.id),
      }));
    } else {
      /**If at max length just return and do nothing */
      if (extras.length === extrasData.menu_item_extra_group_quantity_max) {
        return;
      }
      extras.push({ priceName: pricingOptionName, id: menuId, price });
      setSelectedExtras({
        extras,
        ids: extras.map((ex) => ex.id),
      });
    }
  };
  const renderExtras = () => {
    const { extras, ids } = selectedExtras;
    return extrasData?.menu_item_extra_group_item_id_list.map((extra: any) => {
      const menuItem = menuItemExtras[extra];
      if (
        menuItem?.extra_name_public
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase())
      ) {
        return Object.keys(menuItem?.extra_pricing_options_extras).map((po) => {
          const priceOption: IPricingOptions =
            menuItem?.extra_pricing_options_extras[po];
          const optionName = priceOption.option_name || "";
          const index = ids.indexOf(extra);
          return (
            <ChoiceBox
              key={`${menuItem?.extra_name_public}-${po}`}
              onClick={() =>
                handleExtraChange(menuItem, po, priceOption.option_price)
              }
              choiceBoxName={`${menuItem?.extra_name_public} ${
                optionName ? `(${optionName})` : ""
              }`}
              isSelected={
                index > -1 && extras[index].priceName === po ? true : false
              }
              choiceBoxPricingOption={priceOption}
              menuItem={menuItem}
              setDisplayeExtrasAllergens={DisplayeExtrasAllergens}
            />
          );
        });
      }
    });
  };
  const _renderMinMax = (min: number, max: number) => {
    if (!min && !max) {
      return null;
    } else if (max === 0 || !max) {
      return (
        <p className={Styles.MinMaxText}>
          {T.translate("Menu.MinText")}{" "}
          <strong data-component-name={"extraMin"}>{min}</strong>{" "}
          {min > 1 ? T.translate("Menu.Items") : T.translate("Menu.Item")}:
        </p>
      );
    } else if (min === 0 || !min) {
      return (
        <p className={Styles.MinMaxText}>
          {T.translate("Menu.MaxText")} <strong>{max}</strong>{" "}
          {max > 1 ? T.translate("Menu.Items") : T.translate("Menu.Item")}:
        </p>
      );
    } else if (min && max) {
      return (
        <p className={Styles.MinMaxText}>
          {T.translate("Menu.MinMaxTextPartOne")}{" "}
          <strong data-component-name={"extraMin"}>{min}</strong>
          {min > 1 ? T.translate("Menu.Items") : T.translate("Menu.Item")}
          {T.translate("Menu.MinMaxTextPartTwo")} <strong>{max}</strong>
          {max > 1 ? T.translate("Menu.Items") : T.translate("Menu.Item")}:
        </p>
      );
    } else return null;
  };
  return (
    <div
      className={`${Styles.extraContainer}`}
      style={{
        padding: "1.5rem 1.25rem",
        paddingBottom: "9rem",
      }}
    >
      <div className={Styles.ExtraSearchDiv}>
        <span className="consumer-search" />
        <input
          className={Styles.SearchModsInput}
          placeholder={T.translate("Search.PlaceholderMenu") as string}
          value={searchText}
          onChange={(ev) => setSearchText(ev.target.value)}
        />
      </div>
      {_renderMinMax(
        extrasData.menu_item_extra_group_quantity_min,
        extrasData.menu_item_extra_group_quantity_max
      )}

      {renderExtras()}
    </div>
  );
}

export default React.memo(Extras);
